import React, { useState, useEffect } from "react";
import { MenuItem, Select, TextField, IconButton } from "@mui/material";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import { categories } from "./StockComponentsList";

const StockItems = ({ formData, setFormData }) => {
  const [extraStockItems, setExtraStockItems] = useState(() => {
    return (
      formData.extraStockItems || [
        {
          id: Date.now(),
          category: "",
          subcategory: "",
          productCode: "",
          qty: "",
          totalCost: 0,
          price: 0,
          isPriceFetched: false,
        },
      ]
    );
  });

  // Calculate total cost of all extra stock items
  const calculateTotalExtraStockCost = (items) => {
    return items.reduce((sum, item) => sum + (item.totalCost || 0), 0);
  };

  useEffect(() => {
    const totalExtraStockCost = calculateTotalExtraStockCost(extraStockItems);
    setFormData((prev) => ({ ...prev, extraStockItems, totalExtraStockCost }));
  }, [extraStockItems, setFormData]);

  const addRow = () => {
    setExtraStockItems([
      ...extraStockItems,
      {
        id: Date.now(),
        category: "",
        subcategory: "",
        productCode: "",
        qty: "",
        totalCost: 0,
        price: 0,
        isPriceFetched: false,
      },
    ]);
  };

  const removeRow = (id) => {
    if (extraStockItems.length > 1) {
      setExtraStockItems(extraStockItems.filter((item) => item.id !== id));
    }
  };

  const updateRow = (id, key, value) => {
    setExtraStockItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, [key]: value } : item
      )
    );
  };

  const handleSubcategoryChange = async (id, subcategoryName) => {
    const category = extraStockItems.find((item) => item.id === id)?.category;
    const selectedCategory = categories.find((cat) => cat.label === category);
    const selectedSubcategory = selectedCategory?.subcategories.find(
      (sub) => sub.name === subcategoryName
    );

    const productCode = selectedSubcategory?.productCode || "";
    const price = await fetchPrice(productCode);

    setExtraStockItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id
          ? {
              ...item,
              subcategory: subcategoryName,
              productCode: productCode,
              price: price,
              totalCost:
                price === null
                  ? 0
                  : price * (item.qty ? parseFloat(item.qty) : 0),
              isPriceFetched: true,
            }
          : item
      )
    );
  };

  // Returns the unit price, or null when the product has no price in the
  // system — the row shows "call for pricing" instead of a fake $0.
  const fetchPrice = async (productCode) => {
    try {
      const response = await fetch(
        `/api/doors/kits/getStockItemPrice?productCode=${productCode}`
      );
      const data = await response.json();

      if (response.ok) {
        // Parse the price, round it to 2 decimal places
        const roundedPrice = Math.round(parseFloat(data.price) * 100) / 100;
        return roundedPrice.toFixed(2); // Ensures it's always returned as a string with two decimals
      } else {
        console.error("Error fetching price:", data.error);
        return null;
      }
    } catch (error) {
      console.error("Error during API request:", error);
      return null;
    }
  };

  const handleQtyChange = (id, qty) => {
    if (qty === "" || /^\d*$/.test(qty)) {
      updateRow(id, "qty", qty);

      const item = extraStockItems.find((item) => item.id === id);
      if (item) {
        const totalCost = (item.price || 0) * (qty ? parseInt(qty, 10) : 0);
        updateRow(id, "totalCost", totalCost);
      }
    }
  };

  return (
    <div className="app-card mt-5 overflow-hidden rounded-2xl border">
      <div className="flex flex-wrap items-end justify-between gap-2 border-b app-divider px-5 py-4 sm:px-6">
        <div>
          <h2 className="app-eyebrow">Misc Stock Items</h2>
          <p className="app-muted mt-1 text-xs">
            Extra stock to price with this quote — pick a category, the item,
            and a quantity.
          </p>
        </div>
      </div>
      <div className="space-y-3 px-5 py-5 sm:px-6">
      {extraStockItems.map((item, index) => (
        <div
          key={item.id}
          className="app-surface grid w-full grid-cols-1 items-center gap-3 rounded-xl border p-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)_7rem_minmax(8rem,10rem)_auto]"
        >
          {/* Category Dropdown */}
          <Select
            value={item.category}
            onChange={(e) => updateRow(item.id, "category", e.target.value)}
            displayEmpty
            size="small"
            className="w-full"
            inputProps={{ "aria-label": "Category" }}
          >
            <MenuItem value="">Select Category</MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.label} value={category.label}>
                {category.label}
              </MenuItem>
            ))}
          </Select>

          {/* Subcategory Dropdown */}
          <Select
            value={item.subcategory}
            onChange={(e) => handleSubcategoryChange(item.id, e.target.value)}
            displayEmpty
            size="small"
            className="w-full"
            disabled={!item.category}
            inputProps={{ "aria-label": "Item" }}
          >
            <MenuItem value="">Select Subcategory</MenuItem>
            {categories
              .find((cat) => cat.label === item.category)
              ?.subcategories.map((sub) => (
                <MenuItem key={sub.name} value={sub.name}>
                  {sub.productCode} - {sub.name}
                </MenuItem>
              ))}
          </Select>

          {/* Quantity Input */}
          <TextField
            type="text"
            size="small"
            value={item.qty}
            onChange={(e) => handleQtyChange(item.id, e.target.value)}
            placeholder="Quantity"
            className="w-full"
            inputProps={{ "aria-label": "Quantity", inputMode: "numeric" }}
          />

          {/* Product Price (Read-Only) */}
          <div className="flex min-h-[40px] w-full items-center justify-center gap-1 rounded-xl border app-divider px-3 py-2 text-center">
            {!item.subcategory ? (
              <span className="app-muted text-sm">Select an item</span>
            ) : item.price === null ? (
              <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                No price found — call for pricing
              </span>
            ) : (
              <>
                <span className="app-muted">$</span>
                <span className="font-semibold tabular-nums">
                  {(Number(item.totalCost) || 0).toFixed(2)}
                </span>
              </>
            )}
          </div>

          <div className="flex w-full items-center justify-end gap-1 sm:col-span-2 lg:col-span-1">
            {/* Add Row Button */}
            <IconButton className onClick={addRow} color="primary" aria-label="Add item row">
              <AddCircleOutline />
            </IconButton>

            {/* Remove Row Button (Only for rows beyond the first one) */}
            {extraStockItems.length > 1 ? (
              <IconButton
                onClick={() => removeRow(item.id)}
                color="error"
                aria-label="Remove item row"
              >
                <RemoveCircleOutline />
              </IconButton>
            ) : (
              <IconButton
                onClick={() => {
                  setExtraStockItems([
                    {
                      id: Date.now(),
                      category: "",
                      subcategory: "",
                      productCode: "",
                      qty: "",
                      totalCost: 0,
                      price: 0,
                      isPriceFetched: false,
                    },
                  ]);
                }}
                color="error"
                aria-label="Clear item row"
              >
                <RemoveCircleOutline />
              </IconButton>
            )}
          </div>
        </div>
      ))}
      </div>
    </div>
  );
};

export default StockItems;
